(() => {
  if (customElements.get('size-guide')) return;

  const slugify = (value) =>
    String(value)
      .toLowerCase()
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const readJson = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('size-guide: invalid JSON', error);
      return null;
    }
  };

  const warn = (...args) => {
    if (window.Shopify && window.Shopify.designMode) console.warn('size-guide:', ...args);
  };

  /** Parses "prefix:Label" lines into an ordered list; dropdown order follows it. */
  const parseGenderMap = (text) =>
    String(text || '')
      .split(/\r?\n/)
      .map((line) => {
        const index = line.indexOf(':');
        if (index === -1) return null;
        const prefix = line.slice(0, index).trim();
        const label = line.slice(index + 1).trim();
        return prefix && label ? { prefix: prefix.toLowerCase(), label, slug: slugify(label) } : null;
      })
      .filter(Boolean);

  const isHeaderRow = (row) => typeof row[0] === 'string' && row[0].trim().toLowerCase() === 'size';

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const entries = readJson(this.querySelector('[data-size-guide-entries]')) || [];
      const config = readJson(this.querySelector('[data-size-guide-config]')) || {};

      this.genderSelect = this.querySelector('[data-gender-select]');
      this.typeSelect = this.querySelector('[data-type-select]');
      this.intro = this.querySelector('[data-intro]');
      this.resultBody = this.querySelector('[data-result-body]');
      this.heading = this.querySelector('[data-heading]');
      this.scroller = this.querySelector('[data-scroller]');
      this.table = this.querySelector('[data-table]');
      this.footer = this.querySelector('[data-footer]');
      this.prevButton = this.querySelector('[data-scroll-prev]');
      this.nextButton = this.querySelector('[data-scroll-next]');
      this.htmTrigger = this.querySelector('[data-htm-trigger]');
      this.typePlaceholder = config.typePlaceholder || '';

      /** Cell transform hook (e.g. a future cm/inch toggle); rows in `this.groups` stay raw. */
      this.transformCell = (value) => value;
      this.currentEntry = null;

      this.groups = this.buildGroups(entries, parseGenderMap(config.genderMap));
      this.populateGenders();

      this.genderSelect.addEventListener('change', () => this.onGenderChange());
      this.typeSelect.addEventListener('change', () => this.onTypeChange());
      this.prevButton.addEventListener('click', () => this.scrollTable(-1));
      this.nextButton.addEventListener('click', () => this.scrollTable(1));
      this.scroller.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      this.htmTrigger.addEventListener('click', () => this.openHowToMeasure());

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateOverflow());
        this.resizeObserver.observe(this.scroller);
      } else {
        window.addEventListener('resize', () => this.updateOverflow());
      }

      this.applyUrlParams();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /** Groups entries by mapped gender (first word of product_type), in mapping order. */
    buildGroups(entries, genderMap) {
      const byPrefix = new Map(genderMap.map((gender) => [gender.prefix, { ...gender, types: [] }]));

      entries.forEach((entry) => {
        const words = String(entry.product_type || '').trim().split(/\s+/);
        const prefix = (words.shift() || '').toLowerCase();
        const label = words.join(' ');
        const group = byPrefix.get(prefix);

        if (!group || !label) {
          warn(`skipped "${entry.product_type}" (no gender mapping or no product type)`);
          return;
        }

        const slug = slugify(label);
        if (group.types.some((type) => type.slug === slug)) {
          warn(`duplicate product type "${entry.product_type}" skipped`);
          return;
        }

        group.types.push({ label, slug, entry });
      });

      return [...byPrefix.values()]
        .filter((group) => group.types.length > 0)
        .map((group) => ({
          ...group,
          types: group.types.sort((a, b) => a.label.localeCompare(b.label)),
        }));
    }

    populateGenders() {
      this.groups.forEach((group) => {
        this.genderSelect.append(new Option(group.label, group.slug));
      });
    }

    populateTypes(group) {
      this.typeSelect.replaceChildren(new Option(this.typePlaceholder, ''));
      if (!group) return;
      group.types.forEach((type) => {
        this.typeSelect.append(new Option(type.label, type.slug));
      });
    }

    get selectedGroup() {
      return this.groups.find((group) => group.slug === this.genderSelect.value) || null;
    }

    get selectedType() {
      const group = this.selectedGroup;
      return group ? group.types.find((type) => type.slug === this.typeSelect.value) || null : null;
    }

    onGenderChange({ updateUrl = true } = {}) {
      const group = this.selectedGroup;
      this.populateTypes(group);
      this.typeSelect.disabled = !group;
      this.syncEmptyState();
      this.hideResult();
      if (updateUrl) this.updateUrl();
    }

    onTypeChange({ updateUrl = true } = {}) {
      const type = this.selectedType;
      this.syncEmptyState();
      if (type) {
        this.showEntry(type);
      } else {
        this.hideResult();
      }
      if (updateUrl) this.updateUrl();
    }

    syncEmptyState() {
      this.genderSelect.classList.toggle('is-empty', !this.genderSelect.value);
      this.typeSelect.classList.toggle('is-empty', !this.typeSelect.value);
    }

    showEntry(type) {
      this.currentEntry = type.entry;
      this.heading.textContent = type.entry.heading || type.label;
      this.renderTable(type.entry);
      this.htmTrigger.hidden = !(type.entry.how_to_measure && type.entry.htm_id);
      if (this.intro) this.intro.hidden = true;
      this.resultBody.hidden = false;
      this.scroller.scrollLeft = 0;
      requestAnimationFrame(() => this.updateOverflow());
    }

    hideResult() {
      this.currentEntry = null;
      this.resultBody.hidden = true;
      this.table.replaceChildren();
      if (this.intro) this.intro.hidden = false;
    }

    /** Single render path: raw rows in, table out. Rows render exactly as entered (no transposing). */
    renderTable(entry, transform = this.transformCell) {
      const rows = (entry.rows || [])
        .filter(Array.isArray)
        .map((row) => row.map((cell) => (cell == null ? '' : String(cell))));
      const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
      const hasHeader = rows.length > 1 && isHeaderRow(rows[0]);

      const buildRow = (cells, rowIndex, isHead) => {
        const tr = document.createElement('tr');
        for (let colIndex = 0; colIndex < width; colIndex += 1) {
          const raw = cells[colIndex] ?? '';
          let cell;
          if (isHead) {
            cell = document.createElement('th');
            cell.scope = 'col';
            cell.textContent = raw;
          } else if (colIndex === 0) {
            cell = document.createElement('th');
            cell.scope = 'row';
            cell.textContent = raw;
          } else {
            cell = document.createElement('td');
            cell.textContent = raw === '' ? '' : transform(raw, { entry, rowIndex, colIndex });
          }
          tr.append(cell);
        }
        return tr;
      };

      const fragment = document.createDocumentFragment();
      let bodyRows = rows;

      if (hasHeader) {
        const thead = document.createElement('thead');
        thead.append(buildRow(rows[0], 0, true));
        fragment.append(thead);
        bodyRows = rows.slice(1);
      }

      const tbody = document.createElement('tbody');
      const offset = hasHeader ? 1 : 0;
      bodyRows.forEach((row, index) => tbody.append(buildRow(row, index + offset, false)));
      fragment.append(tbody);

      this.table.style.setProperty('--size-guide-cols', Math.max(width - 1, 1));
      this.table.replaceChildren(fragment);
    }

    updateOverflow() {
      if (this.resultBody.hidden) return;
      const overflows = this.scroller.scrollWidth > this.scroller.clientWidth + 1;
      this.footer.hidden = !overflows;
      this.updateArrows();
    }

    updateArrows() {
      const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
      this.prevButton.disabled = scrollLeft <= 1;
      this.nextButton.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
    }

    scrollTable(direction) {
      const stickyCell = this.table.querySelector('tr > :first-child');
      const visible = this.scroller.clientWidth - (stickyCell ? stickyCell.offsetWidth : 0);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scroller.scrollBy({
        left: direction * Math.max(visible * 0.8, 80),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    openHowToMeasure() {
      const id = this.currentEntry && this.currentEntry.htm_id;
      const dialog = id ? document.getElementById(id) : null;
      if (!dialog) return;
      if (typeof dialog.showDialog === 'function') {
        dialog.showDialog();
      } else {
        const native = dialog.querySelector('dialog');
        if (native && typeof native.showModal === 'function') native.showModal();
      }
    }

    applyUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const genderSlug = slugify(params.get('gender') || '');
      if (!genderSlug || !this.groups.some((group) => group.slug === genderSlug)) return;

      this.genderSelect.value = genderSlug;
      this.onGenderChange({ updateUrl: false });

      const typeSlug = slugify(params.get('type') || '');
      if (typeSlug && this.selectedGroup.types.some((type) => type.slug === typeSlug)) {
        this.typeSelect.value = typeSlug;
        this.onTypeChange({ updateUrl: false });
      }
    }

    updateUrl() {
      const url = new URL(window.location.href);
      const gender = this.genderSelect.value;
      const type = this.typeSelect.value;

      if (gender) url.searchParams.set('gender', gender);
      else url.searchParams.delete('gender');

      if (gender && type) url.searchParams.set('type', type);
      else url.searchParams.delete('type');

      window.history.replaceState(window.history.state, '', url);
    }
  }

  customElements.define('size-guide', SizeGuide);
})();
